import type {
  PowerEvidenceSource,
  PowerFreshness,
  PowerLocation,
  PowerReport,
  PowerSourceKind,
  PowerState,
} from '../types/power'

/**
 * Freshness thresholds, in minutes. Configuration rather than numbers written
 * into components, so a deployment on a less stable grid can shorten them.
 */
export const FRESHNESS_THRESHOLDS: { level: PowerFreshness; withinMinutes: number }[] = [
  { level: 'fresh', withinMinutes: 15 },
  { level: 'recent', withinMinutes: 30 },
  { level: 'aging', withinMinutes: 60 },
]

/** Reports older than this contribute nothing to the current answer. */
export const EVIDENCE_WINDOW_MINUTES = 90

/**
 * How much each kind of evidence counts. A campus facility desk saying the grid
 * is out is worth more than one passing student — but no source is worth so much
 * that a single report cannot be contradicted.
 */
export const EVIDENCE_WEIGHT: Record<PowerEvidenceSource, number> = {
  community: 1,
  verified_report: 1.8,
  property_provider: 2.4,
  campus_admin: 3,
  facility: 3,
  iot: 3.4,
  external_api: 2,
}

/** Report manipulation controls. Enforced in the store, not in the form. */
export const REPORT_LIMITS = {
  /** One person cannot re-report the same location inside this window. */
  perLocationCooldownMinutes: 5,
  /** Total reports one person may file per hour, across all locations. */
  perHour: 6,
}

/** Reliability needs real history before it says anything. */
export const RELIABILITY_MINIMUM = { reports: 24, days: 3 }

export const POWER_STATE_LABEL: Record<PowerState, string> = {
  on: 'ON',
  off: 'OFF',
  unstable: 'Unstable',
  backup: 'Backup',
  unknown: 'Unknown',
}

export const POWER_SOURCE_LABEL: Record<PowerSourceKind, string> = {
  grid: 'Grid',
  generator: 'Generator',
  solar: 'Solar',
  inverter: 'Inverter',
  other: 'Other',
  unknown: 'Source not stated',
}

export const EVIDENCE_LABEL: Record<PowerEvidenceSource, string> = {
  community: 'Student report',
  verified_report: 'Verified resident',
  property_provider: 'Property manager',
  campus_admin: 'Campus staff',
  facility: 'Facility desk',
  iot: 'Connected meter',
  external_api: 'Distribution company feed',
}

/**
 * Campus power locations. These are location ENTITIES, not coordinates belonging
 * to anybody: a report is attached to "Engineering Complex", never to where the
 * reporter was standing. `personal` marks the one location that belongs to the
 * signed-in tenancy, which is never published to others.
 */
export const powerLocations: PowerLocation[] = [
  {
    id: 'pl-lodge',
    name: 'Cedarwood Student Residence',
    kind: 'accommodation',
    campusId: 'campus-akoka',
    lat: 6.5085,
    lng: 3.3928,
    personal: true,
  },
  {
    id: 'pl-campus',
    name: 'UNILAG Main Campus',
    kind: 'campus',
    campusId: 'campus-akoka',
    lat: 6.5158,
    lng: 3.3898,
  },
  {
    id: 'pl-engineering',
    name: 'Engineering Complex',
    kind: 'academic',
    campusId: 'campus-akoka',
    lat: 6.5171,
    lng: 3.3941,
    venueKeywords: ['engineering', 'eng lt', 'engineering lt'],
  },
  {
    id: 'pl-library',
    name: 'Campus Library',
    kind: 'library',
    campusId: 'campus-akoka',
    lat: 6.5149,
    lng: 3.3873,
    venueKeywords: ['library', 'reading room'],
  },
  {
    id: 'pl-student-centre',
    name: 'Student Centre',
    kind: 'social',
    campusId: 'campus-akoka',
    lat: 6.5133,
    lng: 3.3916,
    venueKeywords: ['student centre', 'multipurpose'],
  },
  {
    id: 'pl-ict',
    name: 'ICT Centre',
    kind: 'facility',
    campusId: 'campus-akoka',
    lat: 6.5162,
    lng: 3.3862,
    venueKeywords: ['ict', 'computer', 'lab'],
  },
  {
    id: 'pl-science',
    name: 'Faculty of Science',
    kind: 'academic',
    campusId: 'campus-akoka',
    lat: 6.5177,
    lng: 3.3885,
    venueKeywords: ['science', 'chemistry', 'physics', 'biology'],
  },
  {
    id: 'pl-cafe',
    name: 'Akoka Café',
    kind: 'social',
    campusId: 'campus-akoka',
    lat: 6.5111,
    lng: 3.3902,
  },
  {
    id: 'pl-hostel-a',
    name: 'Hostel Block A',
    kind: 'residence',
    campusId: 'campus-akoka',
    lat: 6.5142,
    lng: 3.3949,
  },
  {
    id: 'pl-onike-studios',
    name: 'Onike Self-Contained Studios',
    kind: 'residence',
    campusId: 'campus-akoka',
    lat: 6.5069,
    lng: 3.3894,
  },
  {
    id: 'pl-lecture-theatre',
    name: 'New Lecture Theatre',
    kind: 'academic',
    campusId: 'campus-akoka',
    lat: 6.5126,
    lng: 3.3877,
    venueKeywords: ['lt1', 'lt2', 'lecture theatre', 'nlt'],
  },
]

export function powerLocationById(id: string): PowerLocation | undefined {
  return powerLocations.find((location) => location.id === id)
}

/**
 * Which Power location each managed property corresponds to. A property with no
 * entry here is genuinely not mapped, and the provider console says so rather
 * than attaching its reports to the nearest plausible location.
 */
export const PROPERTY_POWER_LOCATION: Record<string, string> = {
  'prp-1': 'pl-lodge',
  'prp-2': 'pl-onike-studios',
}

const minutesAgo = (minutes: number) =>
  new Date(Date.now() - minutes * 60_000).toISOString()

let sequence = 0
const report = (
  locationId: string,
  state: PowerState,
  source: PowerSourceKind,
  evidence: PowerEvidenceSource,
  reporterLabel: string,
  minutes: number,
): PowerReport => ({
  id: `pr-seed-${++sequence}`,
  locationId,
  state,
  source,
  evidence,
  reporterLabel,
  reportedAt: minutesAgo(minutes),
})

/**
 * Seeded community evidence. Timed relative to load so freshness is genuinely
 * computed rather than written as a label. Nothing here is a metric: each row is
 * one report, and every figure the interface shows is derived from these.
 */
export const seedPowerReports: PowerReport[] = [
  report('pl-engineering', 'on', 'grid', 'community', 'Student', 4),
  report('pl-engineering', 'on', 'grid', 'community', 'Student', 7),
  report('pl-engineering', 'on', 'generator', 'campus_admin', 'Campus staff', 12),
  report('pl-engineering', 'on', 'grid', 'community', 'Student', 26),
  report('pl-engineering', 'on', 'grid', 'community', 'Student', 41),
  report('pl-engineering', 'off', 'grid', 'community', 'Student', 68),

  report('pl-lodge', 'on', 'grid', 'property_provider', 'Property manager', 4),
  report('pl-lodge', 'on', 'grid', 'community', 'Verified resident', 9),
  report('pl-lodge', 'unstable', 'grid', 'community', 'Verified resident', 52),

  report('pl-campus', 'on', 'grid', 'campus_admin', 'Campus staff', 3),
  report('pl-campus', 'on', 'grid', 'community', 'Student', 14),
  report('pl-campus', 'on', 'grid', 'community', 'Student', 33),

  report('pl-library', 'on', 'grid', 'community', 'Student', 5),
  report('pl-library', 'on', 'grid', 'community', 'Student', 22),

  report('pl-student-centre', 'off', 'grid', 'community', 'Student', 11),
  report('pl-student-centre', 'off', 'grid', 'community', 'Student', 19),
  report('pl-student-centre', 'off', 'grid', 'verified_report', 'Verified resident', 24),

  report('pl-ict', 'backup', 'generator', 'campus_admin', 'Campus staff', 15),
  report('pl-ict', 'backup', 'generator', 'community', 'Student', 4),

  report('pl-hostel-a', 'unstable', 'grid', 'verified_report', 'Verified resident', 6),
  report('pl-hostel-a', 'on', 'grid', 'community', 'Student', 38),

  report('pl-cafe', 'on', 'inverter', 'community', 'Student', 7),

  report('pl-science', 'off', 'grid', 'community', 'Student', 47),
]
